<template>
    <CustomDialog v-model="visible" :defaultTitle="'Filtres avancés'" width="850px" @cancel="close" @confirm="applyFilters">
        <template #content>
            <div class="ppv2-grid ppv2-grid-cols-0 md:ppv2-grid-cols-3 ppv2-gap-2 md:ppv2-gap-4 ppv2-p-4">
                <!-- Row 1 -->
                <div class="ppv2-col-span-2 md:ppv2-col-span-1" v-if="availableFilters.includes('categories')">
                    <BaseSelect
                        v-model="filters.category_ids"
                        :label="labels.categories || 'Catégories'"
                        :placeholder="placeholders.categories || 'Sélectionner des catégories'"
                        :options="categories"
                        optionLabel="name"
                        optionValue="id"
                        :multiple="true"
                        fixed-dropdown
                    />
                </div>
                <div v-if="availableFilters.includes('kitchens')">
                    <BaseSelect
                        v-model="filters.kitchen_ids"
                        :label="labels.kitchens || 'Cuisines / Fournisseurs'"
                        :placeholder="placeholders.kitchens || 'Sélectionner des cuisines'"
                        :options="kitchens"
                        optionLabel="title"
                        optionValue="id"
                        :multiple="true"
                        fixed-dropdown
                    />
                </div>
                <div v-if="availableFilters.includes('brands')">
                    <BaseSelect
                        v-model="filters.brand_ids"
                        :label="labels.brands || 'Marques'"
                        :placeholder="placeholders.brands || 'Sélectionner des marques'"
                        :options="brands"
                        optionLabel="name"
                        optionValue="id"
                        :multiple="true"
                        fixed-dropdown
                    />
                </div>
                <div v-if="availableFilters.includes('status')">
                    <BaseSelect
                        v-model="filters.status"
                        :label="labels.status || 'Statut'"
                        :placeholder="placeholders.status || 'Sélectionner un statut'"
                        :options="statusOptions"
                        optionLabel="label"
                        optionValue="value"
                        fixed-dropdown
                    />
                </div>

                <!-- Row 2 -->
                <div v-if="availableFilters.includes('tags')">
                    <BaseSelect
                        v-model="filters.tag_ids"
                        label="Tags"
                        placeholder="Sélectionner des tags"
                        :options="tags"
                        optionLabel="name"
                        optionValue="id"
                        :multiple="true"
                        fixed-dropdown
                    />
                </div>
                <div v-if="availableFilters.includes('price_range')" class="ppv2-col-span-2">
                    <label class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700 ppv2-mb-1 poppinsMedium"
                        >Fourchette de prix</label
                    >
                    <div class="ppv2-flex ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                        <BaseInput v-model="filters.price_min" type="number" placeholder="Min" classContainerAdd="!ppv2-mb-0" />
                        <span class="ppv2-text-gray-400">-</span>
                        <BaseInput v-model="filters.price_max" type="number" placeholder="Max" classContainerAdd="!ppv2-mb-0" />
                    </div>
                </div>

                <!-- Row 3 (Custom for Tables if needed) -->
                <div v-if="availableFilters.includes('rooms')">
                    <BaseSelect
                        v-model="filters.room_ids"
                        label="Salles"
                        placeholder="Sélectionner des salles"
                        :options="rooms"
                        optionLabel="name"
                        optionValue="id"
                        :multiple="true"
                        fixed-dropdown
                    />
                </div>
            </div>

            <!-- Active Filters Count -->
            <div class="ppv2-mt-4 ppv2-px-4">
                <span class="ppv2-text-sm ppv2-text-blue-500 poppinsRegular"> {{ activeFiltersCount }} filtre(s) actif(s) </span>
            </div>
        </template>

        <template #actions>
            <div class="ppv2-flex ppv2-justify-end ppv2-w-full" :style="{ gap: normalizeSize(16, true) }">
                <button
                    @click="resetFilters"
                    class="ppv2-px-6 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-gray-300 hover:ppv2-bg-gray-50 ppv2-transition-all poppinsRegular"
                >
                    Réinitialiser
                </button>
                <BaseButton @click="applyFilters" variant="primary" :customHeight="43"> Appliquer </BaseButton>
            </div>
        </template>
    </CustomDialog>
</template>

<script>
import normalizeSize from '../../utils/normalizeSize'

import CustomDialog from './CustomDialog.vue'
import BaseSelect from './BaseSelect.vue'
import BaseInput from './BaseInput.vue'
import BaseButton from './BaseButton.vue'

export default {
    name: 'AdvancedFilterModal',
    components: {
        CustomDialog,
        BaseSelect,
        BaseInput,
        BaseButton,
    },
    props: {
        value: { type: Boolean, default: false },
        initialFilters: { type: Object, default: () => ({}) },
        availableFilters: { type: Array, default: () => ['categories', 'kitchens', 'status', 'tags', 'price_range'] },
        categories: { type: Array, default: () => [] },
        kitchens: { type: Array, default: () => [] },
        brands: { type: Array, default: () => [] },
        tags: { type: Array, default: () => [] },
        rooms: { type: Array, default: () => [] },
        labels: { type: Object, default: () => ({}) },
        placeholders: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            filters: {
                category_ids: [],
                kitchen_ids: [],
                brand_ids: [],
                status: 'all',
                tag_ids: [],
                price_min: null,
                price_max: null,
                room_ids: [],
                ...this.initialFilters,
            },
            statusOptions: [
                { label: 'Tous', value: 'all' },
                { label: 'Actif', value: 'active' },
                { label: 'Inactif', value: 'inactive' },
            ],
        }
    },
    computed: {
        visible: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
        activeFiltersCount() {
            let count = 0
            if (this.filters.category_ids?.length > 0) count++
            if (this.filters.kitchen_ids?.length > 0) count++
            if (this.filters.brand_ids?.length > 0) count++
            if (this.filters.status !== 'all') count++
            if (this.filters.tag_ids?.length > 0) count++
            if (this.filters.price_min !== null && this.filters.price_min !== '') count++
            if (this.filters.price_max !== null && this.filters.price_max !== '') count++
            if (this.filters.room_ids?.length > 0) count++
            return count
        },
    },
    watch: {
        initialFilters: {
            handler(newVal) {
                this.filters = { ...this.filters, ...newVal }
            },
            deep: true,
        },
    },
    methods: {
        normalizeSize,
        close() {
            this.visible = false
        },
        applyFilters() {
            this.$emit('apply', { ...this.filters })
            this.close()
        },
        resetFilters() {
            this.filters = {
                category_ids: [],
                kitchen_ids: [],
                brand_ids: [],
                status: 'all',
                tag_ids: [],
                price_min: null,
                price_max: null,
                room_ids: [],
            }
            this.$emit('apply', { ...this.filters })
            this.close()
        },
    },
}
</script>
